/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { ElementType } from 'react'
import { View } from 'react-native'
import styled from 'styled-components/native'
import { v4 as uuidv4 } from 'uuid'

import { AccessibilityRole } from 'libs/accessibilityRole/accessibilityRole'
import { InputError } from 'ui/components/inputs/InputError'
import { Li } from 'ui/components/Li'
import { Ul } from 'ui/components/Ul'
import { ViewGap } from 'ui/components/ViewGap/ViewGap'
import { Checkbox } from 'ui/designSystem/Checkbox/Checkbox'
import {
  CheckboxGroupDisplay,
  CheckboxGroupOption,
  CheckboxGroupProps,
} from 'ui/designSystem/CheckboxGroup/types'
import { SelectableVariant } from 'ui/designSystem/types'
import { Typo } from 'ui/theme'
import { setTextSemantic } from 'ui/theme/typographyAttrs/setTextSemantic'

const labelId = uuidv4()

export const CheckboxGroup = <T extends string = string>({
  label,
  labelTag = 'span',
  labelTagOverrideForAccessibility = undefined,
  description,
  error,
  options,
  value,
  onChange,
  display = 'vertical',
  variant = 'default',
  disabled = false,
}: CheckboxGroupProps<T>) => {
  let LabelTag: ElementType
  switch (labelTag) {
    case 'h1':
      LabelTag = Typo.Title1
      break
    case 'h2':
      LabelTag = Typo.Title2
      break
    case 'h3':
      LabelTag = Typo.Title3
      break
    default:
      LabelTag = Typo.BodyAccent
      break
  }

  const selectedValues = value ?? []
  const handleChange = (option: CheckboxGroupOption<T>, newValue: boolean) => {
    if (disabled) return
    const newValues = newValue
      ? [...selectedValues, option.value]
      : selectedValues.filter((value) => value !== option.value)
    onChange?.(newValues)
  }

  const headingAttrs = labelTagOverrideForAccessibility
    ? setTextSemantic(labelTagOverrideForAccessibility)
    : {}

  return (
    <View accessibilityRole={AccessibilityRole.GROUP} accessibilityLabelledBy={labelId}>
      <Header gap={2} hasError={!!error}>
        <LabelTag {...headingAttrs} id={labelId}>
          {label}
        </LabelTag>
        {description ? <Description>{description}</Description> : null}
        {error ? <InputError errorMessage={error} visible /> : null}
      </Header>

      <CheckboxContainer variant={variant} display={display}>
        {options.map((option) => {
          const isChecked = selectedValues.includes?.(option.value)

          const commonProps = {
            label: option.label,
            isChecked,
            onPress: (newValue: boolean) => handleChange(option, newValue),
            hasError: !!error,
            disabled,
            indeterminate: option.indeterminate,
            required: option.required,
            accessibilityLabel: `${label} - ${option.label}`,
          }

          const computedCheckboxSizing = (() => {
            if (option.sizing) return option.sizing
            if (display === 'horizontal') return 'hug'
            return 'fill'
          })()

          if (variant === 'detailed') {
            if (option.collapsed) {
              return (
                <Li key={option.value}>
                  <Checkbox
                    variant="detailed"
                    sizing="fill"
                    collapsed={option.collapsed}
                    description={option.description}
                    asset={option.asset}
                    {...commonProps}
                  />
                </Li>
              )
            }
            return (
              <Li key={option.value}>
                <Checkbox
                  variant="detailed"
                  sizing={computedCheckboxSizing}
                  description={option.description}
                  asset={option.asset}
                  {...commonProps}
                />
              </Li>
            )
          }
          return (
            <Li key={option.value}>
              <Checkbox variant="default" sizing={computedCheckboxSizing} {...commonProps} />
            </Li>
          )
        })}
      </CheckboxContainer>
    </View>
  )
}

type HasErrorProps = { hasError: boolean }
const Header = styled(ViewGap)<HasErrorProps>(({ theme, hasError }) => ({
  marginBottom: hasError ? theme.designSystem.size.spacing.s : theme.designSystem.size.spacing.l,
}))

const Description = styled(Typo.Body)(({ theme }) => ({
  color: theme.designSystem.color.text.subtle,
}))

type VariantProps = { variant: SelectableVariant }
type DisplayProps = { display: CheckboxGroupDisplay }
const CheckboxContainer = styled(Ul)<VariantProps & DisplayProps>(({ theme, variant, display }) => {
  const isVerticalDisplay = display === 'vertical'
  const isHorizontalDisplay = display === 'horizontal'
  const isDefaultVariant = variant === 'default'
  return {
    flexWrap: isHorizontalDisplay ? 'wrap' : 'nowrap',
    flexDirection: isVerticalDisplay ? 'column' : 'row',
    gap:
      isDefaultVariant || isHorizontalDisplay
        ? theme.designSystem.size.spacing.l
        : theme.designSystem.size.spacing.s,
  }
})
